export const ALL_GROUP_ID = -1;
export const WEEK_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
export const AVATAR_COLORS = ['#409EFF', '#67C23A', '#E6A23C', '#909399', '#F56C6C'];

export const unwrapApi = (res) => {
    if (!res) return res;
    if (res.status != null && res.data && typeof res.data === 'object' && 'code' in res.data) {
        return res.data;
    }
    return res;
};

export const isApiSuccess = (res) => {
    let body = unwrapApi(res);
    return body && (body.code === 0 || body.code === 200);
};

export const apiMsg = (res) => {
    let body = unwrapApi(res);
    return (body && body.msg) || '';
};

export const apiData = (res) => {
    let body = unwrapApi(res);
    return body && body.data;
};

export const pad2 = (n) => String(n).padStart(2, '0');

export const toDateKey = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

export const todayKey = () => {
    let n = new Date();
    return toDateKey(n.getFullYear(), n.getMonth(), n.getDate());
};

export const shiftDateKey = (key, delta) => {
    let p = String(key).split('-').map(Number);
    let d = new Date(p[0], p[1] - 1, p[2] + delta);
    return toDateKey(d.getFullYear(), d.getMonth(), d.getDate());
};

export const getRangeKeys = (a, b) => {
    if (!a || !b) return a || b ? [a || b] : [];
    let start = a < b ? a : b;
    let end = a < b ? b : a;
    let keys = [];
    let cur = start;
    while (cur <= end) {
        keys.push(cur);
        cur = shiftDateKey(cur, 1);
        if (keys.length > 400) break;
    }
    return keys;
};

export const getContiguousSegments = (dates) => {
    let list = (dates || []).slice().sort();
    let segs = [];
    list.forEach((dk) => {
        if (!segs.length || shiftDateKey(segs[segs.length - 1].end, 1) !== dk) {
            segs.push({ start: dk, end: dk });
        } else {
            segs[segs.length - 1].end = dk;
        }
    });
    return segs;
};

export const formatDateLabel = (dateKey) => {
    if (!dateKey) return '';
    let p = String(dateKey).split('-');
    if (p.length < 3) return dateKey;
    return `${Number(p[1])}月${Number(p[2])}日`;
};

export const weekdayLabel = (dateKey) => {
    let p = String(dateKey).split('-').map(Number);
    if (p.length < 3) return '';
    let d = new Date(p[0], p[1] - 1, p[2]);
    return '周' + WEEK_LABELS[d.getDay()];
};

export const parseDateTime = (str) => {
    if (!str) return null;
    let m = String(str).replace('T', ' ').match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
};

export const toDateTimeStr = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

/**
 * 解析用户组 id 列表，兼容 number/string/对象/{id}/JSON字符串
 */
export const parseIdList = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.map((x) => {
            if (x && typeof x === 'object') return Number(x.id);
            return Number(x);
        }).filter((n) => !isNaN(n));
    }
    if (typeof raw === 'string') {
        try { return parseIdList(JSON.parse(raw)); } catch (e) { /* ignore */ }
    }
    return [];
};

export const extractUserGroupIds = (data) => parseIdList(data && data.userGroupIds);

/**
 * 判断是否「全部学员」：
 * 1. 含 -1
 * 2. 已选 id 覆盖全部可选用户组（允许多出历史脏数据）
 * 3. userGroupNames 含「全部学员」
 */
export const isAllGroups = (ids, availableGroups, item) => {
    let list = Array.isArray(ids) ? ids.map(Number) : [];
    if (list.some((id) => id === ALL_GROUP_ID)) return true;
    if (availableGroups && availableGroups.length && list.length) {
        let idSet = new Set(list);
        if (availableGroups.every((g) => idSet.has(Number(g.id)))) return true;
    }
    if (item && Array.isArray(item.userGroupNames) && item.userGroupNames.some((n) => n === '全部学员')) return true;
    return false;
};

/**
 * 归一化用户组 id：如果判定为全选，统一为 [-1]
 */
export const normalizeUserGroupIds = (ids, availableGroups, item) => {
    if (isAllGroups(ids, availableGroups, item)) return [ALL_GROUP_ID];
    return (ids || []).map(Number).filter((n) => !isNaN(n) && n !== ALL_GROUP_ID);
};

export const displayGroupNames = (item, availableGroups) => {
    let ids = extractUserGroupIds(item);
    if (isAllGroups(ids, availableGroups, item)) return ['全部学员'];
    return (item && item.userGroupNames) || [];
};

// ===== 附件封面 =====

export const pickFileExt = (file) => {
    if (!file) return '';
    let e = String(file.ext || '').trim().replace(/^\./, '').toLowerCase();
    if (e) return e;
    let name = file.fileName || file.filename || file.name || '';
    let m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
    return m ? m[1] : '';
};

const COVER_KIND_MAP = {
    png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', bmp: 'image', heic: 'image',
    mp4: 'video', mov: 'video', webm: 'video', m4v: 'video', avi: 'video', mkv: 'video',
    pdf: 'pdf',
    doc: 'word', docx: 'word',
    xls: 'excel', xlsx: 'excel',
    ppt: 'ppt', pptx: 'ppt',
    mp3: 'mp3', wav: 'mp3', ogg: 'mp3', aac: 'mp3', flac: 'mp3',
    zip: 'default', rar: 'default', '7z': 'default', tar: 'default', gz: 'default',
};

export const getAttachCoverKind = (file) => {
    let ext = pickFileExt(file);
    return COVER_KIND_MAP[ext] || 'default';
};

const ICON_MAP = {
    pdf: require('@/assets/images/my/contact/pdf.png'),
    word: require('@/assets/images/my/contact/word.png'),
    excel: require('@/assets/images/my/contact/ex.png'),
    ppt: require('@/assets/images/my/contact/ppt.png'),
    mp3: require('@/assets/images/my/contact/mp3.png'),
    default: require('@/assets/images/my/contact/default.png'),
};

export const getAttachIcon = (kind) => ICON_MAP[kind] || ICON_MAP['default'];

export const videoThumbSrc = (url) => {
    if (!url) return '';
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + '#t=0.1';
};

export const defaultSubmitRange = (dates) => {
    let list = (dates || []).slice().sort();
    if (!list.length) return { start: '', end: '' };
    return {
        start: `${list[0]} 00:00`,
        end: `${list[list.length - 1]} 23:59`,
    };
};

export const filenameFromDisposition = (header, fallback) => {
    if (!header) return fallback;
    let star = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (star) {
        try {
            return decodeURIComponent(star[1].replace(/["']/g, '').trim());
        } catch (e) { /* ignore */ }
    }
    let plain = header.match(/filename=([^;]+)/i);
    if (plain) return plain[1].replace(/["']/g, '').trim();
    return fallback;
};

export const isImageExt = (ext, name) => {
    let e = String(ext || '').replace('.', '').toLowerCase();
    if (!e && name) e = String(name).split('.').pop().toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic'].indexOf(e) >= 0;
};

export const isVideoExt = (ext, name) => {
    let e = String(ext || '').replace('.', '').toLowerCase();
    if (!e && name) e = String(name).split('.').pop().toLowerCase();
    return ['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv', 'flv', 'wmv'].indexOf(e) >= 0;
};

export const overviewUserKey = (user) => `${(user && user.userId) || ''}|${(user && user.fillId) || ''}|${(user && user.userName) || ''}`;

export const overviewUserSub = (user) => {
    if (!user) return '';
    return user.schoolName || user.userGroupName || user.phone || '';
};

export const overviewAvatarColor = (user) => {
    if (!user || !user._submitted) return '#C0C4CC';
    let n = Number(user.userId) || 0;
    return AVATAR_COLORS[n % AVATAR_COLORS.length];
};

export const overviewFeaturedAvatarColor = (user) => {
    let hasWorks = user && ((user.files && user.files.length) || user.fillId);
    if (!hasWorks) return '#C0C4CC';
    let n = Number(user.userId) || 0;
    return AVATAR_COLORS[n % AVATAR_COLORS.length];
};

export const buildOverviewUsers = (overview) => {
    if (!overview) return [];
    let done = (overview.fillList || []).map((u) => ({ ...u, _submitted: true }));
    let todo = (overview.unsubmittedList || []).map((u) => ({ ...u, _submitted: false }));
    return done.concat(todo);
};

export const filterOverviewUsers = (users, filter) => {
    if (filter === 'done') return (users || []).filter((u) => u._submitted);
    if (filter === 'todo') return (users || []).filter((u) => !u._submitted);
    return users || [];
};

export const pickDefaultOverviewUserKey = (users, filter) => {
    let list = users || [];
    if (!list.length) return null;
    if (filter === 'todo') return overviewUserKey(list[0]);
    let submitted = list.filter((u) => u._submitted);
    let first = submitted[0] || null;
    return first ? overviewUserKey(first) : null;
};

export const isMomentFileFeatured = (file) => {
    if (!file) return false;
    let v = file.isFeatured;
    return v === 1 || v === '1' || v === true;
};

export const buildMomentUsers = (overview) => (overview && overview.fillList) || [];

export const filterFeaturedUsers = (users, filter) => {
    let list = users || [];
    if (filter === 'featured') {
        return list.filter((u) => (u.files || []).some(isMomentFileFeatured));
    }
    if (filter === 'normal') {
        return list.filter((u) => (u.files || []).some((f) => !isMomentFileFeatured(f)));
    }
    return list;
};

export const pickDefaultFeaturedUserKey = (users) => {
    let list = users || [];
    if (!list.length) return null;
    return overviewUserKey(list[0]);
};

export const scrollDateChipToCenter = (wrap, activeSelector) => {
    if (!wrap) return;
    let el = wrap.querySelector(activeSelector || '.is-active');
    if (!el) return;
    let wrapRect = wrap.getBoundingClientRect();
    let elRect = el.getBoundingClientRect();
    let delta = (elRect.left + elRect.width / 2) - (wrapRect.left + wrapRect.width / 2);
    let max = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
    wrap.scrollLeft = Math.max(0, Math.min(wrap.scrollLeft + delta, max));
};
